import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { fetchWithAuth, authenticatedFetch, API_ENDPOINT } from '../utils/api';
import { Activity, FileText, X, AlertTriangle, FileCheck, XCircle, Upload, ChevronUp, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { Widget, StatusBadge, Pagination } from './Common';

// Format raw values like "winter_storm" → "Winter Storm", "drill" → "Drill"
function formatDisplayValue(value) {
  if (!value) return '';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function ReportsView({ reports, onReportsChange, onEventClick, onNavigateToNewEvent, cancelling, onCancelProcessing }) {
  const { organization } = useContext(AuthContext);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportContent, setReportContent] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('incident_date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Auto-refresh every 10 seconds if there are processing reports
  useEffect(() => {
    if (!autoRefresh) return;

    const processingReports = reports.filter(r => 
      r.status === 'processing' || r.status === 'pending'
    );

    if (processingReports.length > 0) {
      const interval = setInterval(() => {
        onReportsChange();
      }, 10000); // 10 seconds

      return () => clearInterval(interval);
    }
  }, [reports, autoRefresh, onReportsChange]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, searchTerm]);

  // Filter reports
  const filteredReports = reports.filter(report => {
    const isHistoric = report.is_historic === true || report.status === 'historic';
    
    if (filterType === 'regular' && isHistoric) return false;
    if (filterType === 'historic' && !isHistoric) return false;
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        report.incident_name?.toLowerCase().includes(searchLower) ||
        report.incident_type?.toLowerCase().includes(searchLower) ||
        report.hazard_type?.toLowerCase().includes(searchLower)
      );
    }
    
    return true;
  });

  // Sort filtered reports
  const sortedReports = [...filteredReports].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    
    // Handle date sorting
    if (sortField === 'incident_date' || sortField === 'created_date') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const SortHeader = ({ field, children }) => (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <span className="inline-flex flex-col ml-1">
          {sortField === field ? (
            sortDirection === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-blue-600" /> : <ChevronDown className="w-3.5 h-3.5 text-blue-600" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-gray-300" />
          )}
        </span>
      </div>
    </th>
  );

  const handleCancelProcessing = async (aarId) => {
    // Use the prop function instead of calling loadDashboardData directly
    if (onCancelProcessing) {
      await onCancelProcessing(aarId);
    }
  };

  // Calculate pagination
  const totalItems = sortedReports.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReports = sortedReports.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top on page change
  };

  // Count stats
  const regularCount = reports.filter(r => !(r.is_historic === true || r.status === 'historic')).length;
  const historicCount = reports.filter(r => r.is_historic === true || r.status === 'historic').length;
  const processingCount = reports.filter(r => r.status === 'processing').length;

  const handleViewReport = async (report) => {
    const isHistoric = report.is_historic === true || report.status === 'historic';
    
    if (report.status === 'processing') {
      toast.error('This report is still being analyzed by AI. Please check back in a few minutes.', {
        icon: '⏳',
      });
      return;
    }

    if (report.status === 'failed') {
      toast.error('AI analysis failed for this report. Check CloudWatch logs for details.');
      return;
    }

    setSelectedReport(report);
    setLoadingReport(true);
    setReportContent(null);

    const loadingToast = toast.loading('Loading report...');

    try {
      const data = await fetchWithAuth('/dashboard/reports', organization?.organizationId);
      
      const allReports = data.reports || [];
      const fullReport = allReports.find(r => r.report_id === report.report_id || r.aar_id === report.aar_id);
      
      if (fullReport && fullReport.report_content) {
        setReportContent({
          report_id: fullReport.report_id || fullReport.aar_id,
          aar_id: fullReport.aar_id,
          report_content: fullReport.report_content,
          generated_date: fullReport.generated_date || new Date().toISOString(),
          key_insights: fullReport.key_insights || {},
          is_historic: fullReport.is_historic
        });
        toast.success('Report loaded successfully', { id: loadingToast });
      } else {
        setReportContent({ error: 'Report analysis not yet complete' });
        toast.error('Report analysis not yet complete', { id: loadingToast });
      }
    } catch (error) {
      console.error('Error loading report:', error);
      setReportContent({ error: `Error: ${error.message}` });
      toast.error(`Error loading report: ${error.message}`, { id: loadingToast });
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDeleteReport = async (aarId) => {
    if (!window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
      return;
    }

    const deleteToast = toast.loading('Deleting report...');
    
    try {
      const data = await authenticatedFetch(
        `${API_ENDPOINT}/dashboard/reports/${aarId}?organization_id=${organization?.organizationId}`,
        { method: 'DELETE' }
      );
      
      toast.success('Report deleted successfully! 🗑️', {
        id: deleteToast,
        duration: 3000
      });
      
      onReportsChange();
      
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report', {
        id: deleteToast
      });
    }
  };

  const handleDownloadReport = async (format = 'pdf') => {
    if (!reportContent || !reportContent.report_content) {
      toast.error('No report content to download');
      return;
    }

    const downloadToast = toast.loading(`Generating ${format.toUpperCase()}...`);

    try {
      const buttonSelector = format === 'pdf' ? '[data-pdf-button]' : '[data-word-button]';
      const originalText = document.querySelector(buttonSelector)?.textContent;
      if (document.querySelector(buttonSelector)) {
        document.querySelector(buttonSelector).textContent = 'Generating...';
      }

      const response = await fetch(`${API_ENDPOINT}/generate-document?report_id=${reportContent.report_id}&format=${format}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.download_url) {
        throw new Error('No download URL received from server');
      }

      const downloadLink = document.createElement('a');
      downloadLink.href = data.download_url;
      downloadLink.download = data.filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      toast.success(`${format.toUpperCase()} downloaded successfully!`, { id: downloadToast });

      if (document.querySelector(buttonSelector) && originalText) {
        document.querySelector(buttonSelector).textContent = originalText;
      }

    } catch (error) {
      console.error(`Error downloading ${format.toUpperCase()}:`, error);
      toast.error(`Failed to download ${format.toUpperCase()}: ${error.message}`, { id: downloadToast });
      
      const buttonSelector = format === 'pdf' ? '[data-pdf-button]' : '[data-word-button]';
      if (document.querySelector(buttonSelector)) {
        document.querySelector(buttonSelector).textContent = format === 'pdf' ? 'Download PDF' : 'Download Word';
      }
    }
  };

  const handleCloseModal = () => {
    setSelectedReport(null);
    setReportContent(null);
  };

  const getStatusInfo = (report) => {
    const isHistoric = report.is_historic === true || report.status === 'historic';
    const status = report.status;

    if (status === 'processing') {
      return {
        badge: (
          <span className="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 animate-pulse">
            <Activity className="w-3 h-3 mr-1 animate-spin" />
            Analyzing with AI...
          </span>
        ),
        actions: (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCancelProcessing(report.aar_id);
              }}
              disabled={cancelling === report.aar_id}
              className="text-orange-600 hover:text-orange-900 font-medium disabled:opacity-50"
            >
              {cancelling === report.aar_id ? 'Cancelling...' : 'Cancel'}
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteReport(report.aar_id);
              }}
              className="text-red-600 hover:text-red-900 font-medium"
            >
              Delete
            </button>
          </>
        )
      };
    }

    if (status === 'in_progress') {
      return {
        badge: (
          <span className="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
            <FileText className="w-3 h-3 mr-1" />
            In Progress
          </span>
        ),
        actions: (
          <>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onEventClick(report.aar_id);
              }}
              className="text-blue-600 hover:text-blue-900 font-medium"
            >
              View
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteReport(report.aar_id);
              }}
              className="text-red-600 hover:text-red-900 font-medium"
            >
              Delete
            </button>
          </>
        )
      };
    }

    if (status === 'failed') {
      return {
        badge: (
          <span className="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Analysis Failed
          </span>
        ),
        actions: (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteReport(report.aar_id);
            }}
            className="text-red-600 hover:text-red-900 font-medium"
          >
            Delete
          </button>
        )
      };
    }

    if (status === 'cancelled') {
      return {
        badge: (
          <span className="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
            <XCircle className="w-3 h-3 mr-1" />
            Cancelled
          </span>
        ),
        actions: (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteReport(report.aar_id);
            }}
            className="text-red-600 hover:text-red-900 font-medium"
          >
            Delete
          </button>
        )
      };
    }

    if (isHistoric && status === 'completed') {
      return {
        badge: (
          <span className="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
            <FileCheck className="w-3 h-3 mr-1" />
            Historic ({report.file_count || 0} files)
          </span>
        ),
        actions: (
          <>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleViewReport(report);
              }}
              className="text-blue-600 hover:text-blue-900 font-medium"
            >
              View Analysis
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteReport(report.aar_id, true);
              }}
              className="text-red-600 hover:text-red-900 font-medium"
            >
              Delete
            </button>
          </>
        )
      };
    }

    if (status === 'completed') {
      return {
        badge: <StatusBadge status={status} />,
        actions: (
          <>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleViewReport(report);
              }}
              className="text-blue-600 hover:text-blue-900 font-medium"
            >
              View
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteReport(report.aar_id, false);
              }}
              className="text-red-600 hover:text-red-900 font-medium"
            >
              Delete
            </button>
          </>
        )
      };
    }

    return {
      badge: <StatusBadge status={status} />,
      actions: <span className="text-gray-400">Processing...</span>
    };
  };

  return (
    <>
      <Widget 
        title="All Events" 
        subtitle={`${reports.length} total events`}
        actions={
          <div className="flex items-center space-x-3">
            <button
              onClick={onNavigateToNewEvent}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>New Event</span>
            </button>
            <button
              onClick={onReportsChange}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh</span>
            </button>
          </div>
        }
      >

        {/* Processing Alert Banner */}
        {processingCount > 0 && (
          <div className="mb-4 bg-blue-50 border-l-4 border-blue-400 p-4">
            <div className="flex items-center">
              <Activity className="w-5 h-5 text-blue-400 animate-spin mr-3" />
              <div className="flex-1">
                <p className="text-sm text-blue-700">
                  <strong>{processingCount} report{processingCount > 1 ? 's are' : ' is'}</strong> currently being analyzed by AI.
                  This page will auto-refresh every 10 seconds.
                </p>
              </div>
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`ml-4 px-3 py-1 text-xs rounded ${
                  autoRefresh 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                {autoRefresh ? '✓ Auto-refresh ON' : 'Auto-refresh OFF'}
              </button>
            </div>
          </div>
        )}

        {/* Filter and Search Bar */}
        <div className="mb-6 space-y-4">

          <div className="relative">
            <input
              type="text"
              placeholder="Search by incident name, type, or hazard..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <svg
              className="absolute left-3 top-3 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Reports Table */}
        {paginatedReports.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchTerm 
                ? 'No reports match your search.'
                : filterType === 'regular'
                ? 'No regular AARs found.'
                : filterType === 'historic'
                ? 'No historic records found.'
                : 'No reports available yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortHeader field="incident_name">Incident Name</SortHeader>
                    <SortHeader field="incident_date">Date</SortHeader>
                    <SortHeader field="incident_type">Type</SortHeader>
                    <SortHeader field="hazard_type">Hazard</SortHeader>
                    <SortHeader field="status">Status</SortHeader>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedReports.map(report => {
                    const isHistoric = report.is_historic === true || report.status === 'historic';
                    const statusInfo = getStatusInfo(report);
                    
                    return (
                      <tr 
                        key={report.aar_id} 
                        className={`hover:bg-gray-50 ${isHistoric && report.status === 'completed' ? 'bg-purple-50' : ''}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            {isHistoric && report.status === 'completed' && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                Historic
                              </span>
                            )}
                            <button
                              onClick={() => onEventClick(report.aar_id)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                            >
                              {report.incident_name}
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(report.incident_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDisplayValue(report.incident_type)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDisplayValue(report.hazard_type)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {statusInfo.badge}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          {statusInfo.actions}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Component */}
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </Widget>

      {/* Report Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{selectedReport.incident_name}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {new Date(selectedReport.incident_date).toLocaleDateString()} • {selectedReport.incident_type} • {selectedReport.hazard_type}
                  {selectedReport.is_historic && <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded">Historic</span>}
                </p>
              </div>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingReport ? (
                <div className="flex items-center justify-center py-12">
                  <Activity className="w-8 h-8 text-blue-500 animate-spin mr-3" />
                  <p className="text-gray-600">Loading report...</p>
                </div>
              ) : reportContent?.error ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800">{reportContent.error}</p>
                </div>
              ) : reportContent?.report_content ? (
                <div className="whitespace-pre-wrap text-gray-700 text-sm leading-relaxed font-sans">
                  {reportContent.report_content}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">No report content available</div>
              )}
            </div>
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-500">
                {reportContent?.generated_date ? `Generated: ${new Date(reportContent.generated_date).toLocaleString()}` : ''}
              </p>
              <div className="flex items-center space-x-3">
                <button onClick={handleCloseModal} className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition">
                  Close
                </button>
                <button
                  onClick={() => handleDownloadReport('docx')}
                  disabled={!reportContent?.report_id}
                  data-word-button
                  className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg transition flex items-center space-x-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <FileText className="w-4 h-4" />
                  <span>Download Word</span>
                </button>
                <button
                  onClick={() => handleDownloadReport('pdf')}
                  disabled={!reportContent?.report_id}
                  data-pdf-button
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition flex items-center space-x-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <Upload className="w-4 h-4" />
                  <span>Download PDF</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ReportsView;